import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, FormGroup, IconButton, MenuItem, Slider, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material'
import { CircleSlash, Plus, TrendingDown, X } from 'lucide-react'
import { AppShell, Crumb, Flag as CountryFlag, SkeletonCards, Surface, tokens } from '@agribot/polygon-checks-ui'
import { countryName } from '@agribot/sourcing-model'
import { useScenario } from '../hooks'
import { useCampaigns, type CampaignRow } from '../api'
import { STATUS_LABEL } from '../rules'
import { APPROVAL_GROUPS, decodeShocks, describeStatuses, encodeShocks, type Exposure, type GapLine, type ScenarioBasis, type ScenarioResult, type Shock } from '../scenario'
import { SIGNAL_SOURCES } from '../signals'
import { ScenarioMap } from '../components/ScenarioMap'

const t = (v: number) => `${v.toLocaleString('en-GB', { maximumFractionDigits: 0 })} t`
const share = (part: number, whole: number) => (whole ? `${((part / whole) * 100).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%` : '—')
const name = (code: string) => countryName(code, 'en')

/**
 * Supply-risk scenarios (version 1): "what if Peru's supply fell by 60%, or
 * this supplier stopped?", worked out by the API from the campaign's
 * declared answers (POST /scenario-runs), as are the pickers' choices.
 * A shock can target countries, legal entities, parent companies, or a mix.
 * Shocks are typed in here; version 2 adds ones proposed by outside data
 * (signals.ts), which this screen lists once any source is connected.
 * The scenario lives in the address, so it can be shared as a link.
 */
export function Scenarios() {
  const [params, setParams] = useSearchParams()
  const shocks = useMemo(() => decodeShocks(params.get('shocks')), [params])
  const setShocks = (next: Shock[]) => setParams((p) => {
    const q = new URLSearchParams(p)
    if (next.length) q.set('shocks', encodeShocks(next))
    else q.delete('shocks')
    return q
  }, { replace: true })

  // The campaign the scenario stands on: the link's, else the latest with answers in.
  const campaigns = useCampaigns()
  const linked = params.get('campaign')
  const fallback = useMemo(() => defaultCampaign(campaigns.data ?? []), [campaigns.data])
  const campaignId = linked ?? fallback?.id ?? null
  const setCampaign = (id: string) => setParams((p) => {
    const q = new URLSearchParams(p)
    q.set('campaign', id)
    return q
  }, { replace: true })
  // Put the chosen basis in the address, so copying the link keeps it.
  useEffect(() => { if (!linked && fallback) setCampaign(fallback.id) }, [linked, fallback?.id])
  const listed = (campaigns.data ?? []).find((c) => c.id === campaignId)
  const missing = Boolean(linked && campaigns.data && !listed)

  // Drafts in the basis: asked for with the switch (in the link as drafts=1), or needed by a "not yet approved" shock.
  const draftsAsked = params.get('drafts') === '1'
  const draftsNeeded = shocks.some((s) => s.submissionStatuses?.includes('draft'))
  const setDrafts = (on: boolean) => setParams((p) => {
    const q = new URLSearchParams(p)
    if (on) q.set('drafts', '1')
    else q.delete('drafts')
    return q
  }, { replace: true })

  const { data: result, isPending, isFetching, error } = useScenario(missing ? null : campaignId, shocks, draftsAsked)
  const { countries = [], commodities = [], suppliers = [], parents = [] } = result?.options ?? {}
  const supplierName = (id: string) => suppliers.find((s) => s.id === id)?.name ?? id

  return (
    <AppShell crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Scenarios</Crumb>]}>
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>Supply-risk scenarios</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 6, maxWidth: 760 }}>
          What if a country’s supply fell, or a supplier stopped? Worked out from what suppliers declared for this campaign: the volume each
          origin supplies to each part of each material. A model, not a forecast: it cannot know whether a supplier could switch
          origins, or how fast.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 5, flexWrap: 'wrap' }}>
          <TextField
            select
            label="Campaign"
            value={listed ? campaignId : ''}
            onChange={(e) => setCampaign(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 280, '& .MuiSelect-select': { minHeight: 40, boxSizing: 'border-box', display: 'flex', alignItems: 'center', py: 0 } }}
          >
            {!listed && <MenuItem value="" disabled>{campaigns.isPending ? 'Loading…' : 'Choose a campaign'}</MenuItem>}
            {(campaigns.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name} · {c.status}</MenuItem>)}
          </TextField>
          <Box>
            <FormControlLabel
              control={<Switch checked={draftsAsked || draftsNeeded} disabled={draftsNeeded} onChange={(e) => setDrafts(e.target.checked)} />}
              label="Include draft answers"
            />
            {draftsNeeded && (
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: -1 }}>
                On because a shock covers suppliers still in draft.
              </Typography>
            )}
          </Box>
          {result?.basis && <BasisCaption basis={result.basis} />}
        </Box>
        {missing && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            The campaign in this link no longer exists: it may have been deleted. Choose another campaign above; the shocks are kept.
          </Alert>
        )}
        {!missing && listed?.status === 'closed' && (
          <Alert severity="info" sx={{ mb: 4 }}>
            {listed.name} is closed. The scenario uses its answers as they stood when it closed; a newer campaign may tell a different story.
          </Alert>
        )}
        {campaigns.data && campaigns.data.length === 0 && <Alert severity="info" sx={{ mb: 4 }}>No campaigns yet: a scenario needs a campaign’s answers.</Alert>}
        {error && !missing && <Alert severity="error" sx={{ mb: 4 }}>{(error as Error).message}</Alert>}
        {isPending && campaignId && !missing && !error && <SkeletonCards columns="360px 1fr" />}
        {result && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '360px minmax(0, 1fr)', gap: 6, alignItems: 'start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'sticky', top: 0 }}>
              <ShockBuilder countries={countries} suppliers={suppliers} parents={parents} commodities={commodities} onAdd={(s) => setShocks([...shocks, { ...s, id: `s${Date.now()}` }])} />
              <Surface sx={{ p: 5 }}>
                <Typography variant="h4" component="h2" sx={{ mb: 3 }}>In this scenario</Typography>
                {shocks.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>No shocks yet. Add one above.</Typography>}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {shocks.map((s, i) => (
                    <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5, borderRadius: `${tokens.radius.md}px`, backgroundColor: s.reduction >= 1 ? tokens.color.tintDanger : tokens.color.tintWarning }}>
                      {s.reduction >= 1 ? <CircleSlash size={16} color={tokens.color.danger} /> : <TrendingDown size={16} color={tokens.color.warning} />}
                      <Box sx={{ flex: 1, font: `500 13px ${tokens.font.sans}` }}>
                        {describe(s, supplierName)}: {s.reduction >= 1 ? 'stopped' : `−${Math.round(s.reduction * 100)}%`}
                        {s.commodities?.length ? <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · {s.commodities.join(', ')} only</Box> : null}
                      </Box>
                      <IconButton size="small" aria-label="Remove this shock" onClick={() => setShocks(shocks.filter((_, j) => j !== i))}><X size={14} /></IconButton>
                    </Box>
                  ))}
                </Box>
                {shocks.length > 0 && <Button variant="text" size="small" onClick={() => setShocks([])} sx={{ mt: 2 }}>Clear all</Button>}
              </Surface>
              {SIGNAL_SOURCES.length > 0 && (
                // Version 2: suggestions from outside data appear here once a source is connected.
                <Surface sx={{ p: 5 }}><Typography variant="h4" component="h2">Signals</Typography></Surface>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 4 }}>
                <Kpi label="Volume lost" value={t(result.lostT)} detail={isFetching ? 'Working it out…' : `of ${t(result.totalT)} declared`} tone={result.lostT ? 'danger' : undefined} />
                <Kpi label="Share of the campaign" value={share(result.lostT, result.totalT)} />
                <Kpi label="Ingredients left with nothing" value={String(result.stopped.length)} detail="no other origin" tone={result.stopped.length ? 'danger' : undefined} />
                <Kpi label="Ingredients reduced" value={String(result.reduced.length)} tone={result.reduced.length ? 'warning' : undefined} />
              </Box>
              <ScenarioMap origins={result.origins} />
              {shocks.length > 0 && (
                <>
                  <GapTable title="Left with nothing" help="These ingredients of these materials come only from what the shocks cover. Fallbacks are origins the same group already declares for the same ingredient elsewhere; other suppliers already deliver the same ingredient from untouched origins." lines={result.stopped} empty="Every affected ingredient keeps some supply." />
                  <GapTable title="Reduced" lines={result.reduced} empty="Nothing is only partly affected." />
                  <Breakdown result={result} />
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </AppShell>
  )
}

interface SupplierOption { id: string; name: string; parent: string; tonnes: number }

/** "Pacific Marine Ltd in Peru", "Sunda Ocean Group", "Peru, Chile". */
/**
 * The scenario's default basis: the latest campaign with any answers sent in
 * (submitted, in review, approved or returned), else the latest at all.
 */
export function defaultCampaign(campaigns: CampaignRow[]): CampaignRow | undefined {
  const sent = (c: CampaignRow) => Object.entries(c.submissions ?? {}).some(([status, n]) => status !== 'draft' && Number(n) > 0)
  const latest = [...campaigns].sort((a, b) => String(b.opensAt).localeCompare(String(a.opensAt)))
  return latest.find(sent) ?? latest[0]
}

/** "Based on Supplier survey 2026: 4 approved, 1 submitted; drafts excluded." */
function BasisCaption({ basis }: { basis: ScenarioBasis }) {
  const counts = Object.entries(basis.submissions).filter(([status, n]) => n && (basis.draftsIncluded || status !== 'draft'))
  const drafts = basis.submissions.draft ?? 0
  const label = (status: string) => ((STATUS_LABEL as Record<string, string>)[status] ?? status).toLowerCase()
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
      Based on <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{basis.campaign.name}</Box>
      {': '}
      {counts.length ? counts.map(([status, n]) => `${n} ${label(status)}`).join(', ') : 'no answers sent in yet'}
      {basis.draftsIncluded
        ? (basis.draftsIncludedBy === 'shock' ? '; drafts included, because a shock covers them.' : '; drafts included.')
        : drafts ? `; ${drafts} draft${drafts === 1 ? '' : 's'} left out.` : '; drafts left out.'}
    </Typography>
  )
}

function describe(s: Shock, supplierName: (id: string) => string): string {
  const who = [...(s.supplierIds ?? []).map(supplierName), ...(s.parentCompanies ?? [])].join(', ')
  const where = s.countryCodes.map(name).join(', ')
  const base = who && where ? `${who} in ${where}` : who || where
  if (!s.submissionStatuses?.length) return base
  const approval = describeStatuses(s.submissionStatuses)
  return base ? `${approval}: ${base}` : approval
}

function ShockBuilder({ countries, suppliers, parents, commodities, onAdd }: {
  countries: { code: string; tonnes: number }[]
  suppliers: SupplierOption[]
  parents: { name: string; tonnes: number }[]
  commodities: string[]
  onAdd: (s: Omit<Shock, 'id'>) => void
}) {
  const [picked, setPicked] = useState<{ code: string; tonnes: number }[]>([])
  const [entities, setEntities] = useState<SupplierOption[]>([])
  const [groups, setGroups] = useState<{ name: string; tonnes: number }[]>([])
  const [approval, setApproval] = useState<string[]>([])
  const scoped = picked.length + entities.length + groups.length + approval.length > 0
  const [only, setOnly] = useState<string[]>([])
  const [pct, setPct] = useState(100)
  return (
    <Surface sx={{ p: 5, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography variant="h4" component="h2">Add a shock</Typography>
      <Autocomplete
        multiple
        options={countries}
        value={picked}
        onChange={(_, v) => setPicked(v)}
        getOptionLabel={(o) => name(o.code)}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.code} sx={{ display: 'flex', gap: 2 }}>
            <CountryFlag code={o.code} size={12} /><Box sx={{ flex: 1 }}>{name(o.code)}</Box>
            <Box sx={{ color: 'text.secondary', fontSize: 12 }}>{t(o.tonnes)}</Box>
          </Box>
        )}
        renderTags={(v, getTagProps) => v.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.code} size="small" label={name(o.code)} />)}
        renderInput={(params) => <TextField {...params} label="Countries" placeholder={picked.length ? '' : 'Any country'} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
      />
      <Autocomplete
        multiple
        options={suppliers}
        value={entities}
        onChange={(_, v) => setEntities(v)}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.id} sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}><Box>{o.name}</Box>{o.parent !== o.name && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{o.parent}</Box>}</Box>
            <Box sx={{ color: 'text.secondary', fontSize: 12 }}>{t(o.tonnes)}</Box>
          </Box>
        )}
        renderTags={(v, getTagProps) => v.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.id} size="small" label={o.name} />)}
        renderInput={(params) => <TextField {...params} label="Legal entities" placeholder={entities.length ? '' : 'Any supplier'} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
      />
      <Autocomplete
        multiple
        options={parents}
        value={groups}
        onChange={(_, v) => setGroups(v)}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.name === b.name}
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.name} sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>{o.name}</Box><Box sx={{ color: 'text.secondary', fontSize: 12 }}>{t(o.tonnes)}</Box>
          </Box>
        )}
        renderTags={(v, getTagProps) => v.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.name} size="small" label={o.name} />)}
        renderInput={(params) => <TextField {...params} label="Parent companies" placeholder={groups.length ? '' : 'Any group'} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
      />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>Suppliers not yet approved</Typography>
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mb: 1 }}>
          How much rests on answers nobody has approved yet. Tick the states to cover.
        </Typography>
        <FormGroup row sx={{ gap: 0 }}>
          {APPROVAL_GROUPS.map((g) => (
            <FormControlLabel
              key={g.key}
              label={g.label}
              control={<Checkbox size="small" checked={approval.includes(g.key)} onChange={(e) => setApproval(e.target.checked ? [...approval, g.key] : approval.filter((k) => k !== g.key))} />}
              sx={{ mr: 3, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            />
          ))}
        </FormGroup>
        <Button variant="text" size="small" onClick={() => setApproval(APPROVAL_GROUPS.map((g) => g.key))} sx={{ px: 0 }}>All not yet approved</Button>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: -2 }}>
        Pick at least one. Several narrow each other: a legal entity and a country means that entity’s origins in that country.
      </Typography>
      <Autocomplete
        multiple
        options={commodities}
        value={only}
        onChange={(_, v) => setOnly(v)}
        renderTags={(v, getTagProps) => v.map((o, i) => <Chip {...getTagProps({ index: i })} key={o} size="small" label={o} />)}
        renderInput={(params) => <TextField {...params} label="Only these commodities (optional)" placeholder={only.length ? '' : 'All commodities'} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
      />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>{pct >= 100 ? 'Supply stops completely' : `Supply falls by ${pct}%`}</Typography>
        <Slider
          value={pct}
          onChange={(_, v) => setPct(v as number)}
          min={5}
          max={100}
          step={5}
          marks={[{ value: 25, label: '−25%' }, { value: 50, label: '−50%' }, { value: 75, label: '−75%' }, { value: 100, label: 'Stop' }]}
          aria-label="How much supply falls"
          getAriaValueText={(v) => (v >= 100 ? 'stopped' : `${v}% less`)}
          sx={{ mx: 1, width: 'calc(100% - 16px)' }}
        />
      </Box>
      <Button
        startIcon={<Plus size={16} />}
        disabled={!scoped}
        onClick={() => {
          onAdd({
            source: 'manual', countryCodes: picked.map((p) => p.code), supplierIds: entities.map((e) => e.id),
            parentCompanies: groups.map((g) => g.name), commodities: only, reduction: pct / 100,
            ...(approval.length ? { submissionStatuses: APPROVAL_GROUPS.filter((g) => approval.includes(g.key)).flatMap((g) => g.statuses) } : {}),
          })
          setPicked([]); setEntities([]); setGroups([]); setOnly([]); setApproval([])
        }}
      >
        Add to scenario
      </Button>
    </Surface>
  )
}

function Kpi({ label, value, detail, tone }: { label: string; value: string; detail?: string; tone?: 'danger' | 'warning' }) {
  return (
    <Surface sx={{ p: 4 }}>
      <Typography variant="overline" component="div" sx={{ color: 'text.disabled', mb: 1 }}>{label}</Typography>
      <Box sx={{ font: `600 24px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums', color: tone === 'danger' ? tokens.color.danger : tone === 'warning' ? tokens.color.warning : 'text.primary' }}>{value}</Box>
      {detail && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{detail}</Typography>}
    </Surface>
  )
}

function GapTable({ title, help, lines, empty }: { title: string; help?: string; lines: GapLine[]; empty: string }) {
  return (
    <Surface>
      <Box sx={{ px: 5, pt: 4, pb: 2 }}>
        <Typography variant="h4" component="h2">{title} · {lines.length}</Typography>
        {help && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{help}</Typography>}
      </Box>
      {lines.length === 0 ? <Typography variant="body2" sx={{ px: 5, pb: 4, color: 'text.secondary' }}>{empty}</Typography> : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Material · ingredient</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>From</TableCell>
                <TableCell align="right">Lost</TableCell>
                <TableCell>Fallbacks declared</TableCell>
                <TableCell>Other suppliers of this ingredient</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.key} sx={{ '& td': { height: 52 } }}>
                  <TableCell><Box sx={{ fontWeight: 500, color: 'text.primary' }}>{l.material}</Box><Box sx={{ fontSize: 12, color: 'text.secondary' }}>{l.ingredient}</Box></TableCell>
                  <TableCell><Box>{l.supplier}</Box><Box sx={{ fontSize: 12, color: 'text.secondary' }}>{l.parent}</Box></TableCell>
                  <TableCell>{l.countries.map(name).join(', ')}</TableCell>
                  <TableCell align="right"><Box sx={{ fontWeight: 500 }}>{t(l.lostT)}</Box><Box sx={{ fontSize: 12, color: 'text.secondary' }}>of {t(l.ingredientT)}</Box></TableCell>
                  <TableCell>
                    {l.fallbacks.length
                      ? l.fallbacks.map((f) => <Box key={f.origin} sx={{ fontSize: 13 }}>{f.origin} ({name(f.country)})</Box>)
                      : <Box component="span" sx={{ color: 'text.secondary' }}>None declared</Box>}
                  </TableCell>
                  <TableCell>
                    {l.alternatives.length
                      ? l.alternatives.map((a) => <Box key={a.supplier} sx={{ fontSize: 13 }}>{a.supplier} <Box component="span" sx={{ color: 'text.secondary' }}>· {t(a.tonnes)}</Box></Box>)
                      : <Box component="span" sx={{ color: 'text.secondary' }}>None in this campaign</Box>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Surface>
  )
}

function Breakdown({ result }: { result: ScenarioResult }) {
  const [by, setBy] = useState<'byParent' | 'bySupplier' | 'byMaterial' | 'byCommodity'>('byParent')
  const rows: Exposure[] = result[by]
  return (
    <Surface>
      <Tabs value={by} onChange={(_, v) => setBy(v)} sx={{ px: 4 }}>
        <Tab value="byParent" label="By parent company" />
        <Tab value="bySupplier" label="By legal entity" />
        <Tab value="byMaterial" label="By material" />
        <Tab value="byCommodity" label="By commodity" />
      </Tabs>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{by === 'byCommodity' ? 'Commodity' : by === 'byMaterial' ? 'Material' : 'Company'}</TableCell>
            <TableCell align="right">Lost</TableCell>
            <TableCell align="right">Declared</TableCell>
            <TableCell sx={{ width: 200 }}>Share lost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((e) => (
            <TableRow key={e.key} sx={{ '& td': { height: 48 } }}>
              <TableCell><Box sx={{ color: 'text.primary', fontWeight: 500 }}>{e.label}</Box>{e.detail && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{e.detail}</Box>}</TableCell>
              <TableCell align="right">{t(e.lostT)}</TableCell>
              <TableCell align="right">{t(e.totalT)}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 100, height: 6, borderRadius: 999, backgroundColor: tokens.color.ink100, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${(e.lostT / Math.max(e.totalT, 1)) * 100}%`, backgroundColor: e.lostT >= e.totalT ? tokens.color.danger : tokens.color.dataRisk.med }} />
                  </Box>
                  {share(e.lostT, e.totalT)}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Surface>
  )
}
